// Sales records list — Receipts / On-Hold Receipts (cash) and Wholesales
// Invoices / On-Hold Invoices (credit). One component, four configurations:
//
//   <sales-records .storeCode=... kind="sales|hold" mode="cash|credit">
//
// Actions: view detail (lines + payments), print/download (HTML receipt for
// cash, server PDF for invoices), edit (posted rows → "trade-edit" event the
// hub turns into a POS load that saves back to the same record), recall
// (holds → "trade-recall" event the hub turns into a POS load), delete
// (confirm; server enforces permissions).
// Desktop parity: posted lists default to today, on-hold lists show all.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { TradeSalesRow } from '../lib/api.js';
import { OutletViewCache, invalidateOutletView } from '../lib/outlet-view-cache.js';
import { serverEvents, debounce, LIST_CHANGE_CODES, type ServerEvent } from '../lib/events.js';
import { printReceipt } from '../lib/receipt-print.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface KeptRecordsView {
  from: string;
  to: string;
  rows: TradeSalesRow[];
  error: string;
  notice: string;
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const dayStartMs = (iso: string) => new Date(`${iso}T00:00:00`).getTime();
const dayEndMs = (iso: string) => new Date(`${iso}T23:59:59.999`).getTime();

const fmtDate = (ms: number) => (ms ? new Date(ms).toLocaleString() : '—');
const fmtMoney = (n: number | undefined) => (Number(n) || 0).toFixed(2);

@customElement('sales-records')
export class SalesRecords extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .toolbar {
      display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.6rem;
      padding: 0.75rem 1rem 0.6rem; flex-shrink: 0;
    }
    .toolbar .fg { margin-bottom: 0; }
    .toolbar input[type='date'] { min-width: 140px; }
    .spacer { flex: 1; }
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0 1rem 1rem; }
    .table-wrap { flex: 1; min-height: 0; overflow: auto; border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); }
    .table-wrap thead th {
      position: sticky; top: 0; z-index: 1;
      background: var(--color-surface); border-bottom: none;
      box-shadow: inset 0 -1px 0 var(--color-border);
    }
    .row-actions { display: flex; gap: 0.3rem; justify-content: flex-end; }
    .empty { padding: 2rem 1rem; text-align: center; color: var(--color-text-secondary); font-size: 0.85rem; }
    .notice { margin: 0 1rem 0.5rem; font-size: 0.8rem; color: var(--color-success, #188038); }
    .err { margin: 0 1rem 0.5rem; font-size: 0.8rem; color: var(--color-error, #d93025); }
    .hold-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #fff3e0; color: #b26a00; font-size: 0.68rem; font-weight: 700; }

    /* detail modal */
    .modal-overlay {
      position: fixed; inset: 0; background: rgba(15,23,42,0.5);
      display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem;
    }
    .modal {
      background: var(--color-surface); border-radius: 16px; width: 100%; max-width: 760px;
      max-height: 88dvh; display: flex; flex-direction: column; overflow: hidden;
      box-shadow: 0 24px 60px rgba(15,35,80,0.3);
    }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: none; border: none; border-radius: 10px; font-size: 1.2rem; cursor: pointer; color: var(--color-text-secondary); }
    .modal-close:hover { background: #f4f7fb; color: var(--color-text); }
    .modal-body { padding: 1rem 1.1rem; overflow-y: auto; }
    .modal-footer { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.5rem 1rem; margin-bottom: 1rem; }
    .detail-grid .lbl { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-secondary); }
    .detail-grid .val { font-size: 0.86rem; }
    .lines-wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; max-height: 320px; margin-bottom: 0.9rem; }
    .totals { display: flex; flex-direction: column; align-items: flex-end; gap: 0.2rem; font-size: 0.86rem; }
    .totals .grand { font-weight: 700; font-size: 1rem; }
    @media (max-width: 768px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh; }
      .modal-footer { padding-bottom: calc(0.9rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) kind: 'sales' | 'hold' = 'sales';
  @property({ type: String }) mode: 'cash' | 'credit' = 'cash';

  @state() private _from = todayIso();
  @state() private _to = todayIso();
  @state() private _rows: TradeSalesRow[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _notice = '';
  @state() private _detail: TradeSalesRow | null = null;
  @state() private _detailBusy = false;

  private _eventUnsubs: Array<() => void> = [];
  private _reloadSoon = debounce(() => void this._load(), 800);
  /** Server signalled a change that a completed load has not applied yet. */
  private _staleAfterEvent = false;

  private _view = new OutletViewCache<KeptRecordsView>(this, 'trade-records', {
    capture: () => ({
      from: this._from,
      to: this._to,
      rows: this._rows,
      error: this._error,
      notice: this._notice,
    }),
    restore: (v) => {
      this._from = v.from;
      this._to = v.to;
      this._rows = v.rows;
      this._error = v.error;
      this._notice = v.notice;
      this._loading = false;
      this._detail = null;
    },
    reset: () => {
      this._onReset();
    },
  });

  connectedCallback() {
    super.connectedCallback();
    // Live list refresh — same behaviour as the desktop receipt windows
    // (ServerEventMapping sales/salesHold → wpEVT_DB_RELOAD_*_RECEIPT_LIST):
    // only this outlet's events, only codes that change the rows.
    this._eventUnsubs = [
      serverEvents.on('sales', this._onServerEvent),
      serverEvents.on('salesHold', this._onServerEvent),
      serverEvents.on('salesWholesales', this._onServerEvent),
      serverEvents.on('salesWholesalesHold', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reloadSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    this._view.dispose();
    // A change event was seen but its debounced reload never ran (the user
    // left the tab first): do not keep the pre-event rows as this outlet's
    // kept view — the next visit should refetch.
    if (this._staleAfterEvent) {
      this._staleAfterEvent = false;
      invalidateOutletView('trade-records', `${this.storeCode}|${this.kind}|${this.mode}`);
    }
  }

  /** Event type that feeds this list configuration (kind + mode). */
  private get _eventType(): string {
    if (this.mode === 'credit') return this.kind === 'hold' ? 'salesWholesalesHold' : 'salesWholesales';
    return this.kind === 'hold' ? 'salesHold' : 'sales';
  }

  /** ppServer data-change event — reload when this outlet's list changed. */
  private _onServerEvent = (ev: ServerEvent) => {
    if (ev.store && ev.store !== this.storeCode) return;
    if (ev.typeName !== this._eventType) return;
    if (!LIST_CHANGE_CODES.has(ev.code)) return;
    this._staleAfterEvent = true;
    this._reloadSoon();
  };

  /** Socket (re)connected or ppServer came back — events may have been missed. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync' || !this.storeCode) return;
    this._staleAfterEvent = true;
    this._reloadSoon();
  };

  protected updated() {
    const result = this._view.switchTo(this.storeCode ? `${this.storeCode}|${this.kind}|${this.mode}` : '');
    if (result === 'unchanged') return;
    this._reloadSoon.cancel();
    this._staleAfterEvent = false;
    if (result === 'fresh') void this._load();
  }

  private _onReset() {
    this._from = todayIso();
    this._to = todayIso();
    this._rows = [];
    this._error = '';
    this._notice = '';
    this._loading = false;
    this._detail = null;
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    this._notice = '';
    try {
      const res = await api.tradeSalesList(this.storeCode, {
        type: this.mode,
        method: this.kind === 'hold' ? 'suspend' : 'sales',
        from: this.kind === 'hold' ? 0 : dayStartMs(this._from),
        to: this.kind === 'hold' ? 32472144000000 - 1 : dayEndMs(this._to),
      });
      if (!this._view.isCurrent(epoch)) return;
      this._rows = res.data ?? [];
      this._staleAfterEvent = false;
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private async _openDetail(row: TradeSalesRow) {
    const epoch = this._view.epoch;
    this._detailBusy = true;
    this._error = '';
    try {
      const res = await api.tradeSalesDetail(this.storeCode, row.id, this.kind === 'hold' ? 'suspend' : 'sales');
      if (!this._view.isCurrent(epoch)) return;
      this._detail = res.data;
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load detail';
    } finally {
      if (this._view.isCurrent(epoch)) this._detailBusy = false;
    }
  }

  private async _print(row: TradeSalesRow) {
    // Suspended sales never print a receipt (desktop parity) — this action is
    // only reachable for posted records; holds offer Recall + DRAFT invoice PDF.
    const epoch = this._view.epoch;
    try {
      const res = await api.tradeSalesDetail(this.storeCode, row.id, this.kind === 'hold' ? 'suspend' : 'sales');
      if (!this._view.isCurrent(epoch)) return;
      printReceipt(res.data, this._storeName(), { wholesale: this.mode === 'credit' });
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load receipt';
    }
  }

  private async _downloadPdf(row: TradeSalesRow) {
    const epoch = this._view.epoch;
    try {
      const blob = await api.tradeSalesInvoicePdf(this.storeCode, row.id, {
        method: this.kind === 'hold' ? 'suspend' : 'sales',
      });
      if (!this._view.isCurrent(epoch)) return;
      this._downloadBlob(blob, `invoice_${row.id}`);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Invoice failed';
    }
  }

  private _downloadBlob(blob: Blob, base: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  private _storeName(): string {
    // The outlet display name is provided by the shell via sessionStorage
    // session info; fall back to the store code when unavailable.
    try {
      const raw = sessionStorage.getItem('ppos_session');
      if (raw) {
        const s = JSON.parse(raw);
        const db = s?.ownedDBs?.find((x: any) => x.code === this.storeCode);
        if (db?.name) return db.name;
      }
    } catch { /* ignore */ }
    return this.storeCode;
  }

  /** Recall an on-hold receipt/invoice into the POS screen. */
  private _recall(row: TradeSalesRow) {
    this._loadIntoEntry(row, false);
  }

  /** Edit a posted receipt/invoice (holds have no Edit — use Recall). */
  private _edit(row: TradeSalesRow) {
    this._loadIntoEntry(row, this.kind === 'sales');
  }

  private _loadIntoEntry(row: TradeSalesRow, edit: boolean) {
    // List rows carry no lines — fetch the full record before handing it to
    // the POS screen (the detail modal already has them and skips the fetch).
    // Posted edits use "trade-edit" so the entry saves back to the same id;
    // holds use "trade-recall" (new record; the entry deletes the old hold as
    // soon as it loads, so other terminals can no longer recall it).
    const eventName = edit ? 'trade-edit' : 'trade-recall';
    const dispatch = (full: TradeSalesRow) => this.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: { tradeKind: 'sale', row: full, edit },
    }));
    if (row.lines && row.lines.length > 0) {
      dispatch(row);
      return;
    }
    void (async () => {
      const epoch = this._view.epoch;
      try {
        const res = await api.tradeSalesDetail(this.storeCode, row.id, this.kind === 'hold' ? 'suspend' : 'sales');
        if (!this._view.isCurrent(epoch)) return;
        dispatch(res.data);
      } catch (e: any) {
        if (!this._view.isCurrent(epoch)) return;
        this._error = e?.message || (edit ? 'Failed to load the record' : 'Failed to load the hold');
      }
    })();
  }

  private async _delete(row: TradeSalesRow) {
    const label = this.kind === 'hold'
      ? `${this.mode === 'credit' ? 'invoice' : 'receipt'} hold #${row.seqNo || row.id}`
      : `${this.mode === 'credit' ? 'invoice' : 'receipt'} #${row.seqNo || row.id}`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.tradeDeleteSale(this.storeCode, row.id, this.kind === 'hold' ? 'suspend' : 'sales');
      if (!this._view.isCurrent(epoch)) return;
      this._notice = `Deleted ${label}`;
      this._detail = null;
      void this._load();
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Delete failed';
    }
  }

  private _paidBy(row: TradeSalesRow): string {
    const names = (row.payments ?? []).map((p) => p.paidByName || p.paidByCode).filter(Boolean);
    return names.length ? names.join(', ') : '—';
  }

  render() {
    return html`
      <div class="toolbar">
        ${this.kind === 'sales' ? html`
          <div class="fg">
            <label>From</label>
            <input type="date" .value=${this._from} ?disabled=${this._loading}
              @change=${(e: Event) => { this._from = (e.target as HTMLInputElement).value; }}>
          </div>
          <div class="fg">
            <label>To</label>
            <input type="date" .value=${this._to} ?disabled=${this._loading}
              @change=${(e: Event) => { this._to = (e.target as HTMLInputElement).value; }}>
          </div>
        ` : html`
          <span class="hold-badge">On hold — showing all</span>
        `}
        <button class="btn btn-sm" ?disabled=${this._loading} data-testid="sales-reload" @click=${() => void this._load()}>
          ${this._loading ? 'Loading…' : '↻ Reload'}
        </button>
        <span class="spacer"></span>
      </div>
      ${this._error ? html`<div class="err">⚠ ${this._error}</div>` : ''}
      ${this._notice ? html`<div class="notice">✓ ${this._notice}</div>` : ''}

      <div class="tab-body">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Ref</th>
                <th>Channel</th>
                <th>${this.mode === 'credit' ? 'Customer' : 'Member'}</th>
                <th class="num">Total</th>
                <th>Paid By</th>
                <th style="width:1%"></th>
              </tr>
            </thead>
            <tbody>
              ${this._rows.length === 0 && !this._loading ? html`
                <tr><td colspan="8"><div class="empty">No records.</div></td></tr>
              ` : this._rows.map((row) => html`
                <tr class="clickable" data-testid="sales-row" data-row-id=${row.id} @click=${() => void this._openDetail(row)}>
                  <td>${row.seqNo || row.id}</td>
                  <td>${fmtDate(row.date)}</td>
                  <td>${row.refNo || '—'}</td>
                  <td>${this._originLabel(row.origin)}</td>
                  <td>${row.customerName || '—'}</td>
                  <td class="num">${fmtMoney(row.total)}</td>
                  <td>${this._kind === 'hold' ? this._holdPaidBy() : this._paidBy(row)}</td>
                  <td @click=${(e: Event) => e.stopPropagation()}>
                    <div class="row-actions">
                      ${this.kind === 'sales' ? html`
                        <button class="btn btn-sm" data-testid="sales-edit" @click=${() => this._edit(row)}>Edit</button>
                      ` : ''}
                      ${this.kind === 'hold' ? html`
                        <button class="btn btn-sm" data-testid="sales-recall" @click=${() => this._recall(row)}>Recall</button>
                      ` : html`
                        <button class="btn btn-sm" @click=${() => this._print(row)}>Print</button>
                      `}
                      <button class="btn btn-sm" data-testid="sales-pdf" @click=${() => this._downloadPdf(row)}>PDF</button>
                      <button class="btn btn-sm btn-danger" data-testid="sales-delete" @click=${() => void this._delete(row)}>Del</button>
                    </div>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </div>

      ${this._detail || this._detailBusy ? this._renderDetail() : ''}
    `;
  }

  private get _kind(): string {
    return this.kind;
  }

  /** Human label for the channel of origin (legacy rows read as desktop). */
  private _originLabel(origin?: string): string {
    if (origin === 'web') return 'Web';
    if (origin === 'self-service') return 'Self-service';
    if (origin === 'desktop') return 'Cashier';
    return origin || 'Cashier';
  }

  /**
   * A hold has no payment yet — the cashier takes it on recall, so the payment
   * type stays Cash (credit for a wholesales hold). The hold's note (e.g. the
   * mall order reference) lives in remark/note, not here.
   */
  private _holdPaidBy(): string {
    return this.mode === 'credit' ? 'Credit' : 'Cash';
  }

  private _renderDetail() {
    const d = this._detail;
    return html`
      <div class="modal-overlay" @click=${() => { if (!this._detailBusy) this._detail = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>
              ${this.mode === 'credit' ? 'Invoice' : 'Receipt'}
              ${d ? `#${d.seqNo || d.id}` : ''}
              ${this.kind === 'hold' ? html`<span class="hold-badge" style="margin-left:0.4rem">On hold</span>` : ''}
            </h3>
            <button class="modal-close" @click=${() => { this._detail = null; }}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._detailBusy ? html`<div class="empty">Loading…</div>` : !d ? '' : html`
              <div class="detail-grid">
                <div><div class="lbl">Date</div><div class="val">${fmtDate(d.date)}</div></div>
                <div><div class="lbl">${this.mode === 'credit' ? 'Customer' : 'Member'}</div><div class="val">${d.customerName || '—'}</div></div>
                <div><div class="lbl">Ref</div><div class="val">${d.refNo || '—'}</div></div>
                <div><div class="lbl">Channel</div><div class="val">${this._originLabel(d.origin)}</div></div>
                <div><div class="lbl">Cashier</div><div class="val">${d.userName || '—'}</div></div>
                <div><div class="lbl">Term</div><div class="val">${d.term || 0}</div></div>
                <div><div class="lbl">Terminal</div><div class="val">${d.terminalName || '—'}</div></div>
                ${(d.remark || d.note) ? html`<div><div class="lbl">Remark</div><div class="val">${d.remark || d.note}</div></div>` : ''}
              </div>
              <div class="lines-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th><th>Item</th><th class="num">Qty</th><th>Pack</th>
                      <th class="num">Price</th><th class="num">Total</th>
                      ${this.mode === 'credit' ? html`<th>Batch</th><th>Expiry</th>` : ''}
                    </tr>
                  </thead>
                  <tbody>
                    ${(d.lines ?? []).map((l) => html`
                      <tr>
                        <td>${l.code}</td>
                        <td>${l.name}</td>
                        <td class="num">${l.quantity}${l.bonusQty ? ` +${l.bonusQty}` : ''}</td>
                        <td>${l.packDesc || l.pack}</td>
                        <td class="num">${fmtMoney(l.price)}</td>
                        <td class="num">${fmtMoney(l.value)}</td>
                        ${this.mode === 'credit' ? html`
                          <td>${l.batchNo || '—'}</td>
                          <td>${l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : '—'}</td>
                        ` : ''}
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
              <div class="totals">
                <div>Subtotal <strong>${fmtMoney(d.total)}</strong></div>
                ${Number(d.tax) ? html`<div>Tax <strong>${fmtMoney(d.tax)}</strong></div>` : ''}
                ${Number(d.rounding) ? html`<div>Rounding <strong>${fmtMoney(d.rounding)}</strong></div>` : ''}
                ${Number(d.discountAtTotal) ? html`<div>Discount <strong>${fmtMoney(d.discountAtTotal)}</strong></div>` : ''}
                <div class="grand">Due ${fmtMoney(d.total + d.tax + d.rounding + d.discountAtTotal)}</div>
                ${(d.payments ?? []).map((p) => html`
                  <div>${p.paidByName || p.paidByCode} <strong>${fmtMoney(p.amount)}</strong></div>
                `)}
              </div>
            `}
          </div>
          <div class="modal-footer">
            ${d && this.kind === 'sales' ? html`
              <button class="btn btn-sec" @click=${() => { this._detail = null; this._edit(d); }}>✎ Edit</button>
            ` : ''}
            ${d && this.kind === 'hold' ? html`
              <button class="btn btn-sec" @click=${() => { this._detail = null; this._recall(d); }}>Recall</button>
            ` : ''}
            ${d && this.mode !== 'credit' && this.kind !== 'hold' ? html`
              <button class="btn btn-sec" @click=${() => printReceipt(d, this._storeName())}>🖨 Print</button>
            ` : ''}
            ${d ? html`
              <button class="btn btn-sec" @click=${() => void this._downloadPdf(d)}>⬇ PDF</button>
              <button class="btn btn-danger" @click=${() => void this._delete(d)}>Delete</button>
            ` : ''}
            <button class="btn-cancel" @click=${() => { this._detail = null; }}>Close</button>
          </div>
        </div>
      </div>
    `;
  }
}
